import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ToolPage,
  ToolHeader,
  CollectionSummary,
  ProjectCollection,
} from "../../ToolPage";
import { useTaskStart } from "../../../features/toolExperience/useTaskStart";
import { ConfirmModal } from "../../Modal/Modal";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../hooks/useToast";
import {
  useClusteringProjects,
  useCreateClusteringProject,
  useDeleteClusteringProject,
  useUpdateClusteringProject,
} from "../../../hooks/useClustering";
import type { ProjectCreate, ProjectRead } from "../../../types/clustering";
import { canMutate } from "../shared";
import { ProjectCreateModal } from "./ProjectCreateModal";

export function ProjectsView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();
  const mutate = canMutate(user);

  const {
    data: projects = [],
    isLoading,
    error,
    refetch,
  } = useClusteringProjects();
  const createMut = useCreateClusteringProject();
  const updateMut = useUpdateClusteringProject();
  const deleteMut = useDeleteClusteringProject();

  const [modalState, setModalState] = useState<
    { mode: "create" } | { mode: "edit"; project: ProjectRead } | null
  >(null);
  const [deleteTarget, setDeleteTarget] = useState<ProjectRead | null>(null);

  const handleSubmit = async (body: ProjectCreate) => {
    try {
      if (modalState?.mode === "edit") {
        await updateMut.mutateAsync({ id: modalState.project.id, body });
        toast.success("Project updated");
      } else {
        const created = await createMut.mutateAsync(body);
        toast.success(
          "Project created",
          `"${created.name}" is ready for clustering`,
        );
        taskStart.created(created);
      }
      setModalState(null);
    } catch (err) {
      toast.error("Save failed", (err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success("Project deleted");
      setDeleteTarget(null);
    } catch (err) {
      toast.error("Delete failed", (err as Error).message);
    }
  };

  const taskStart = useTaskStart({
    projects,
    toolId: "clustering",
    onSelect: (project) =>
      navigate(`/clustering/${encodeURIComponent(project.id)}/create`),
    onCreate: () => setModalState({ mode: "create" }),
    loading: isLoading,
  });
  const consumedStart = useRef("");
  useEffect(() => {
    if (
      new URLSearchParams(location.search).get("start") !== "clustering" ||
      isLoading ||
      error ||
      !mutate ||
      consumedStart.current === location.key
    )
      return;
    consumedStart.current = location.key;
    const params = new URLSearchParams(location.search);
    params.delete("start");
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true },
    );
    taskStart.begin();
  }, [
    location.key,
    location.pathname,
    location.search,
    isLoading,
    error,
    mutate,
    navigate,
    taskStart,
  ]);

  return (
    <ToolPage toolId="clustering">
      <ToolHeader
        title="Keyword Clustering"
        description="Group keywords into content opportunities."
        primaryAction={
          mutate
            ? {
                label: "Cluster keywords",
                onSelect: taskStart.begin,
                disabledReason:
                  isLoading || !!error
                    ? "Wait for projects to load"
                    : undefined,
              }
            : undefined
        }
        secondaryActions={
          mutate
            ? [
                {
                  label: "New project",
                  onSelect: () => setModalState({ mode: "create" }),
                  testId: "clustering-create-project",
                },
              ]
            : []
        }
      />
      <CollectionSummary
        scope="complete"
        items={[{ label: "projects", value: error ? null : projects.length }]}
      />
      <ProjectCollection
        toolId="clustering"
        loading={isLoading}
        error={error ? String(error) : null}
        onRetry={() => {
          void refetch();
        }}
        items={projects.map((p) => ({
          id: p.id,
          title: p.name,
          description: p.description || undefined,
          href: `/clustering/${encodeURIComponent(p.id)}`,
          testId: `clustering-project-${p.id}`,
          updatedAt: p.updated_at || p.created_at,
          metadata: [
            {
              label: p.updated_at ? "Updated" : "Created",
              value: new Date(
                p.updated_at || p.created_at,
              ).toLocaleDateString(),
            },
          ],
          actions: mutate
            ? [
                {
                  label: "Cluster keywords",
                  onSelect: () =>
                    navigate(`/clustering/${encodeURIComponent(p.id)}/create`),
                },
                {
                  label: "Rename",
                  onSelect: () => setModalState({ mode: "edit", project: p }),
                },
                { label: "Delete", onSelect: () => setDeleteTarget(p) },
              ]
            : [],
        }))}
      />
      {taskStart.chooser}

      {modalState && (
        <ProjectCreateModal
          isOpen={true}
          initial={modalState.mode === "edit" ? modalState.project : null}
          isSubmitting={createMut.isPending || updateMut.isPending}
          onClose={() => {
            taskStart.cancel();
            setModalState(null);
          }}
          onSubmit={handleSubmit}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete project?"
        message={`"${deleteTarget?.name ?? ""}" and all its batches will be soft-deleted. This cannot be undone from the UI.`}
        confirmText="Delete"
        variant="danger"
      />
    </ToolPage>
  );
}
